import { normalizeRoleScope, resolveRoleIdFromValue } from '../config/rbacConfig';
import { extractScopeSeed } from '../utils/scopeFields';

const SESSION_KEY = 'nlims.session';
const APPROVED_STATUS_KEYS = [
  'data_status',
  'status',
  'validation_status',
  'workflow_status',
  'approval_status',
];

const WORKFLOW_STATUS_FIELDS = [
  'data_status',
  'dataStatus',
  'validation_status',
  'validationStatus',
  'workflow_status',
  'workflowStatus',
  'approval_status',
  'approvalStatus',
  'inspection_status',
  'inspectionStatus',
  'verification_status',
  'verificationStatus',
  'resolution_status',
  'resolutionStatus',
];

const HIDDEN_WORKFLOW_STATUSES = new Set([
  'draft',
  'planned',
  'submitted',
  'submit',
  'pending',
  'pending_approval',
  'pending_validation',
  'awaiting_approval',
  'awaiting_validation',
  'pending_review',
  'review',
  'under_review',
  'validate',
  'validating',
  'validated',
  'in_progress',
  'inprogress',
  'in_review',
  'ongoing',
  'delayed',
  'on_hold',
  'waiting',
  'queued',
  'rejected',
  'reject',
  'revoked',
  'revoking',
  'recalled',
  'recall',
  'cancelled',
  'canceled',
  'suspended',
  'withdrawn',
]);

function normalizeStatusToken(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function hasWorkflowHint(row = {}) {
  return WORKFLOW_STATUS_FIELDS.some((field) => {
    if (field === 'status' || field === 'status_label') return false;
    const value = row?.[field];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

function readViewerRowStatus(row = {}) {
  for (const field of [...WORKFLOW_STATUS_FIELDS, 'status', 'status_label']) {
    const value = row?.[field];
    if (value === undefined || value === null) continue;
    const token = normalizeStatusToken(value);
    if (token) return token;
  }
  return '';
}

function normalizeRoleId(value) {
  return resolveRoleIdFromValue(value);
}

function readStoredSession() {
  if (typeof window === 'undefined') return null;

  let raw = null;
  try {
    raw =
      window.localStorage.getItem(SESSION_KEY) ||
      window.sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getSessionUser(session) {
  return session?.user ?? session?.profile ?? session?.data?.user ?? session?.data ?? session ?? null;
}

function getSessionScopeSeed(user = {}) {
  return extractScopeSeed(user);
}

export function getCurrentRoleId() {
  const session = readStoredSession();
  return normalizeRoleId(
    session?.user?.roleId ??
    session?.user?.role_id ??
    session?.roleId ??
    session?.role_id ??
    session?.user?.roles?.[0]?.roleId ??
    session?.user?.roles?.[0]?.role_id ??
    session?.user?.roles?.[0]?.code
  );
}

export function getCurrentUserScope(roleId = getCurrentRoleId()) {
  const session = readStoredSession();
  const user = getSessionUser(session) ?? {};
  const scope = getSessionScopeSeed(user);
  return normalizeRoleScope(roleId, scope);
}

export function clampPayloadToCurrentScope(payload = {}, roleId = getCurrentRoleId()) {
  const next = { ...payload };
  const resolvedRoleId = normalizeRoleId(roleId) ?? getCurrentRoleId();
  const scope = getCurrentUserScope(resolvedRoleId);

  if (resolvedRoleId === 'R04') {
    if (scope.state) next.state = scope.state;
    return next;
  }

  if (resolvedRoleId === 'R05') {
    if (scope.state) next.state = scope.state;
    if (scope.lga) next.lga = scope.lga;
    return next;
  }

  return next;
}

export function applyScopedParams(params = {}, roleId = getCurrentRoleId()) {
  const resolvedRoleId = normalizeRoleId(roleId) ?? getCurrentRoleId();
  return clampPayloadToCurrentScope(params, resolvedRoleId);
}

export function filterItemsByCurrentScope(items = []) {
  const rows = Array.isArray(items) ? items : [];
  const roleId = getCurrentRoleId();

  if (roleId !== 'R04' && roleId !== 'R05') {
    return rows;
  }

  const scope = getCurrentUserScope(roleId);
  const scopedState = String(scope.state ?? '').trim().toLowerCase();
  const scopedLga = String(scope.lga ?? '').trim().toLowerCase();

  return rows.filter((row) => {
    if (scopedState && String(row?.state ?? '').trim().toLowerCase() !== scopedState) {
      return false;
    }

    if (roleId === 'R05' && scopedLga && String(row?.lga ?? '').trim().toLowerCase() !== scopedLga) {
      return false;
    }

    return true;
  });
}

export function isReadOnlyApprovedViewer(roleId = getCurrentRoleId()) {
  return normalizeRoleId(roleId) === 'R06';
}

export function applyApprovedOnlyParams(params = {}, statusKeys = APPROVED_STATUS_KEYS) {
  const next = { ...params };

  if (!isReadOnlyApprovedViewer()) {
    return next;
  }

  for (const key of statusKeys) {
    if (key) next[key] = 'approved';
  }

  return next;
}

export function shouldHideRowForApprovedViewer(row = {}) {
  if (!isReadOnlyApprovedViewer()) return false;

  const status = readViewerRowStatus(row);
  if (!status) return false;

  if (!hasWorkflowHint(row) && !HIDDEN_WORKFLOW_STATUSES.has(status)) {
    return false;
  }

  return HIDDEN_WORKFLOW_STATUSES.has(status);
}

export function filterApprovedViewerRows(items = []) {
  const rows = Array.isArray(items) ? items : [];
  if (!isReadOnlyApprovedViewer()) return rows;
  return rows.filter((row) => !shouldHideRowForApprovedViewer(row));
}
